import { resolveDisplayCurrency } from '@/lib/currency/resolveDisplayCurrency'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'
import { selectDisplayPrice, type VATPreference } from '@/vat/vatUtils'

type AddToCartOptionLabelOption = {
  description: string
  price: number
  salePrice?: number | null
  priceIncVat?: number | null
  salePriceIncVat?: number | null
  taxType?: number | null
  currencySymbol?: string | null
  currencyCode?: string | null
  inStock: boolean
}

export const hasValidOptionSalePrice = (option: AddToCartOptionLabelOption) => {
  const salePrice = option.salePrice ?? null
  return (
    typeof salePrice === 'number' && salePrice > 0 && salePrice < option.price
  )
}

export const formatAddToCartOptionLabel = ({
  option,
  hostBrand,
  vatPreference,
  isPreorder,
}: {
  option: AddToCartOptionLabelOption
  hostBrand?: RegionalBrandCode | null
  vatPreference: VATPreference
  isPreorder: boolean
}) => {
  const hasValidSale = hasValidOptionSalePrice(option)
  const optionCurrency = resolveDisplayCurrency({
    hostBrand,
    currencyCode: option.currencyCode,
    currencySymbol: option.currencySymbol,
  })
  const displayPriceResult = selectDisplayPrice(
    hasValidSale ? option.salePrice : option.price,
    hasValidSale
      ? (option.salePriceIncVat ?? option.priceIncVat)
      : option.priceIncVat,
    vatPreference,
    optionCurrency.currencyCode,
    option.taxType
  )
  const originalPriceResult = hasValidSale
    ? selectDisplayPrice(
        option.price,
        option.priceIncVat,
        vatPreference,
        optionCurrency.currencyCode,
        option.taxType
      )
    : null
  const vatSuffix =
    displayPriceResult.suffix && displayPriceResult.suffix !== 'VAT exempt'
      ? ` (${displayPriceResult.suffix})`
      : ''
  const stockSuffix = !option.inStock && !isPreorder ? ' (out of stock)' : ''
  const priceText =
    hasValidSale && originalPriceResult
      ? `${optionCurrency.currencySymbol}${displayPriceResult.price.toFixed(
          2
        )} (was ${optionCurrency.currencySymbol}${originalPriceResult.price.toFixed(
          2
        )})`
      : `${optionCurrency.currencySymbol}${displayPriceResult.price.toFixed(2)}`

  return `${option.description}: ${priceText}${vatSuffix}${stockSuffix}`
}
